import { Console, Effect, Fiber, pipe } from "effect";
import { RewardsData } from "./types";
import {
  Address,
  getChainKeyFromProtocol,
  isProdProtocolKey,
  makeUsd,
  ProdProtocolKeyService,
  ProtocolKeyService,
  USD_ZERO,
} from "@silo/types";
import { getNativeRewards } from "./get-native-rewards";
import { MerklClient } from "../merkl-client";

export type GetRewardsData = ReturnType<typeof getRewardsData>;
export type GetRewardsDataSuccess = Effect.Effect.Success<GetRewardsData>;
export type GetRewardsDataError = Effect.Effect.Error<GetRewardsData>;
export type GetRewardsDataContext = Effect.Effect.Context<GetRewardsData>;

export function getRewardsData(user: Address) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const chainKey = getChainKeyFromProtocol(protocolKey);

    const nativeRewardsFiber = yield* Effect.fork(getNativeRewards(user));

    const merklRewardsFiber = yield* Effect.fork(
      isProdProtocolKey(protocolKey)
        ? pipe(
            MerklClient.getUserRewardsInfo(user),
            Effect.provideService(ProdProtocolKeyService, protocolKey)
          )
        : Effect.succeed(null)
    );

    const nativeRewards = yield* Fiber.join(nativeRewardsFiber);

    const merklRewards = yield* pipe(
      Fiber.join(merklRewardsFiber),
      Effect.tapError((error) =>
        Effect.logError(`User merkl rewards failed with ${error._tag}`)
      ),
      Effect.tapError((error) => Console.log(error.message)),
      Effect.catchAll(() => Effect.succeed(null))
    );

    const totalRewardsUsd = pipe(
      (merklRewards?.totalUsd ?? USD_ZERO) +
        (nativeRewards?.balanceUsd ?? USD_ZERO),
      makeUsd
    );

    const rewardsData: RewardsData = {
      data: {
        protocolKey,
        chainKey,
        user,
        totalRewardsUsd,
        nativeRewards,
        merklRewards,
      },
    };

    return rewardsData;
  });
}
